import {
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TypographyProps,
  useTheme,
} from '@mui/material';

import { Icon, IconType } from '../../Icon/Icon';

export type SidebarItemDetail = {
  /**
   * @props {string} id - The id of the sidebar.
   */
  id: string;

  /**
   * @props { 'noIcon' | 'blank' | 'usersIcon'  | 'organizationsIcon  | 'studiesIcon'  | 'accuracyIcon'  | 'cohortsIcon'} icon

   */
  icon: 'noIcon' | ('blank' | IconType);

  /**
   * @props {string} label - The label of the sidebar item.
   * @default ''
   */
  label: string;

  /**
   * @props {string} secondaryLabel - The secondary label of the sidebar item.
   * @default ''
   */
  secondaryLabel?: string;

  /**
   * @props {boolean} sectionLabel - style the label for a section label
   * @default false
   *
   */
  sectionLabel?: boolean;

  /**
   * @props {boolean} selected - Whether the sidebar item is selected.
   * @default false
   */
  selected?: boolean;

  /**
   * @props {boolean} disabled - Whether the sidebar item is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * @props {boolean} seleactable - Whether the sidebar item is isSelectable.
   * @default true
   */
  isSelectable?: boolean;

  /**
   * @props {boolean} divider - is a divider displayed after the item
   * @default false
   */
  divider?: boolean;

  /**
   * @props {() => void} onClick - The function to call when the sidebar item is clicked.
   * @default () => {}
   */
  onClick?: () => void;
};

export type SidebarItemProps = SidebarItemDetail & {
  /**
   * @props {boolean} isSidebarOpen - Whether the sidebar is open.
   * @default false
   */
  isSidebarOpen?: boolean;
};

export const SidebarItem = (props: SidebarItemProps) => {
  const {
    id,
    isSidebarOpen,
    icon,
    label,
    secondaryLabel = '',
    sectionLabel = false,
    selected = false,
    disabled = false,
    isSelectable = true,
    divider = false,
    onClick,
  } = props;

  const theme = useTheme();

  const sectionHeaderProps: TypographyProps<'span', { component?: 'span' }> = {
    fontWeight: 500,
    fontSize: 12,
    color: 'text.secondary',
    variant: 'body1',
    textTransform: 'uppercase',
    noWrap: true,
    component: 'span',
  };

  const secondaryLabelProps: TypographyProps<'span', { component?: 'span' }> = {
    fontWeight: 'bold',
    color: selected ? 'text.primary' : 'text.secondary',
    variant: 'body1',
    noWrap: true,
    component: 'span',
  };

  return (
    <>
      <ListItem
        data-testid={`${id}-sidebar-listItem`}
        key={`${id.replace(/[^a-zA-Z0-9]/g, '')}`}
        disablePadding
        role={'listitem'}
      >
        <ListItemButton
          data-testid={`${id}-sidebar-listItemButton`}
          style={{ pointerEvents: isSelectable ? 'auto' : 'none' }}
          sx={{
            height: theme.spacing(12),
            px: 5,
          }}
          disabled={disabled}
          selected={selected}
          role="button"
          onClick={() => {
            onClick && isSelectable && !disabled && onClick();
          }}
        >
          {icon !== 'noIcon' && icon !== 'blank' && (
            <ListItemIcon
              data-testid={`${id}-sidebar-listItemIcon-${icon.replace(/[^a-zA-Z0-9]/g, '')}`}
              sx={{
                mr: isSidebarOpen ? 4 : 'auto',
                minWidth: 0,
                justifyContent: 'center',
              }}
            >
              <Icon id={`${id}-sidebar-icon-${icon.replace(/[^a-zA-Z0-9]/g, '')} `} icon={icon} />
            </ListItemIcon>
          )}
          <ListItemText
            data-testid={`${id}-sidebar-text-${label.replace(/[^a-zA-Z0-9]/g, '')}`}
            primary={label}
            secondary={secondaryLabel}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: isSidebarOpen ? 1 : 0,
              '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                display: 'inline',
                marginRight: label ? '8px' : 'none', // Adjust the spacing between the primary and secondary text as needed
              },
            }}
            primaryTypographyProps={{
              fontWeight: selected ? 700 : 'normal',
              color: selected ? 'text.primary' : 'text.secondary',
              variant: 'body1',
              noWrap: true,
            }}
            disableTypography={false}
            secondaryTypographyProps={sectionLabel ? sectionHeaderProps : secondaryLabelProps}
          />
        </ListItemButton>
      </ListItem>
      {/* This is 100% width */}
      {divider && <Divider sx={{ width: '100%' }} />}
    </>
  );
};
